import { Vector3 } from 'three';
import { angleBetween, slerpUnit, tangentToward, travel } from '../planet/geo';

// The guest's Muse on a visit keeps its friend company: it stands a step to one side of the host, on dry ground, and
// faces it; when the host has wandered off more than a little, it picks a new spot beside it and walks there. While
// the host keeps walking, the guest walks abreast of it, on the side it was on when the host set off: its spot goes
// with the host, a step to that side and a touch ahead of where the host is, and it walks a little brisker than the
// host until it is there; with no room beside the host (a narrow pier, a porch by its wall), it walks in single file
// behind it, on ground the host has just walked, never crossing over through it.
// Pure: it reads only what it is given and keeps only where the guest stands, where it is going, and how the host has
// been moving.

export const FOLLOW = {
  /** How far beside the host the guest stands (m). */
  side: 1.3,
  /** How far the host may wander from where the guest last chose its spot before the guest moves (m). */
  leash: 0.6,
  /** Nearer than this to the guest's spot (m), the host is in its way: the guest steps aside to a new spot. */
  near: 0.9,
  /** The guest's walking pace (m/s): a little brisker than the host's, so it keeps up. */
  pace: 0.75,
  /** Its quickest pace (m/s), catching up with a host that walks on. */
  run: 1.3,
  /** The host walks while it moves faster than this (m/s); faster than `carried` it was carried there, not walking. */
  walking: 0.15,
  carried: 2.5,
  /** How long the host's pace takes to read (s): a step's sway is not a walk. */
  settle: 0.3,
  /** How far ahead of the host's place a guest walking with it aims, in the host's own time (s). */
  lead: 0.4,
  /** Farther than this from its spot (m), the guest is simply there: the visit began, or the host was carried off. */
  jump: 12,
  /** A spot nearer than this (m) is where the guest already stands: it doesn't set off for it. */
  arrive: 0.02,
  /** Ground this far from the shore (m) is dry enough to stand on. */
  dry: 0.3,
} as const;

/** Bearings round the host to try for a spot (rad from the side preferred, by turns): that side, the other, then behind and between. */
const AROUND = [0, Math.PI, Math.PI / 2, Math.PI / 4, (3 * Math.PI) / 4, -Math.PI / 4, (-3 * Math.PI) / 4, -Math.PI / 2];
/** The rings to try them on (× FOLLOW.side): a step away, then a little nearer, then farther out (a host on a narrow pier). */
const RINGS = [1, 0.75, 1.3, 1.7, 2.2];
/** Beside a host that walks (× FOLLOW.side), then behind it in single file. */
const ABREAST = [1, 0.75, 1.3];
const BEHIND = [1, 1.3, 1.7, 2.2];
/** Nearer than this to a host's way (m, to one side of it), the guest stands in its way as it sets off. */
const IN_THE_WAY = 0.5;

export interface HostAt {
  /** Where the host stands: a unit direction. */
  readonly dir: Vector3;
  /** The way it faces there: a unit tangent. */
  readonly heading: Vector3;
}

/** The guest this frame: where it stands, what it faces or walks toward, and whether it is walking (1) or not (0). */
export interface Following {
  readonly dir: Vector3;
  readonly toward: Vector3;
  readonly walk: 0 | 1;
}

const step = new Vector3();
const going = new Vector3();
const ahead = new Vector3();

export class Follower {
  private at: Vector3 | null = null;
  private spot: Vector3 | null = null;
  /** Where the host stood when the spot was chosen. */
  private anchor: Vector3 | null = null;
  private walking = false;
  /** Where the host stood last frame, and how it has been moving (planet space, m/s: along the ground). */
  private last: Vector3 | null = null;
  private readonly motion = new Vector3();
  private readonly toward = new Vector3();
  /** The side the guest walks on while the host walks (+1 its left, −1 its right; 0 while the host stands). */
  private sideSign: 0 | 1 | -1 = 0;

  /**
   * One frame, `dt` seconds on: the host as it stands now, the planet's radius (m), and whether a place is dry
   * enough to stand on.
   */
  step(host: HostAt, dt: number, radius: number, dry: (dir: Vector3) => boolean): Following {
    const speed = this.read(host, dt, radius);
    const moving = speed > FOLLOW.walking;
    if (moving && this.at) {
      // walking with it: a step to the side it was on when the host set off, a touch ahead of where the host is now
      going.copy(this.motion).divideScalar(speed);
      if (this.sideSign === 0) this.sideSign = setOffSide(host.dir, going, this.at, radius, dry);
      travel(host.dir, going, Math.min(speed * FOLLOW.lead, FOLLOW.side) / radius, ahead);
      this.spot = spotAbreast(host.dir, going, ahead, this.sideSign, radius, dry);
      this.anchor = host.dir.clone();
    } else {
      this.sideSign = 0;
      const wandered = !this.spot || !this.anchor || angleBetween(this.anchor, host.dir) * radius > FOLLOW.leash;
      if (wandered || !this.spot || angleBetween(this.spot, host.dir) * radius < FOLLOW.near) {
        this.spot = spotBeside(host, radius, dry, this.at);
        this.anchor = host.dir.clone();
      }
    }
    if (!this.at || angleBetween(this.at, this.spot) * radius > FOLLOW.jump) {
      this.at = this.spot.clone();
      this.walking = false;
    }
    // it sets off once its spot is more than a hair away, then walks at its pace until it stands on it; with a host
    // that walks on, it walks along with it, a little brisker the farther behind it is
    const left = angleBetween(this.at, this.spot) * radius;
    if (left > FOLLOW.arrive) this.walking = true;
    const pace = moving ? Math.min(FOLLOW.run, Math.max(FOLLOW.pace, speed + 0.8 * left)) : FOLLOW.pace;
    if (this.walking && left > 0) {
      const stride = pace * Math.min(Math.max(dt, 0), 0.1);
      slerpUnit(this.at, this.spot, Math.min(1, stride / left), this.at);
      if (stride >= left && !moving) this.walking = false;
    }
    if (!moving && left <= 0) this.walking = false;
    const walking = this.walking;
    // (walking: toward its spot, or, once abreast of a host that walks on, the way the host goes)
    const toward = !walking ? host.dir : left > 0.05 || !moving ? this.spot : travel(this.at, this.motion.clone().divideScalar(speed), 1 / radius, this.toward);
    return { dir: this.at, toward, walk: walking ? 1 : 0 };
  }

  /** The host's pace (m/s) as it has been moving: smoothed over FOLLOW.settle; a move faster than a walk was a jump, not a walk. */
  private read(host: HostAt, dt: number, radius: number): number {
    const last = this.last;
    this.last = host.dir.clone();
    if (!last) return 0;
    const moved = angleBetween(last, host.dir) * radius;
    if (dt <= 0 || moved > FOLLOW.carried * dt) {
      // carried, or the same moment: no walk to read
      if (moved > 0) this.motion.set(0, 0, 0);
      return this.motion.length();
    }
    step.subVectors(host.dir, last).multiplyScalar(radius / dt);
    step.addScaledVector(host.dir, -step.dot(host.dir));
    this.motion.addScaledVector(host.dir, -this.motion.dot(host.dir));
    this.motion.lerp(step, 1 - Math.exp(-dt / FOLLOW.settle));
    return this.motion.length();
  }

  /** Forget where the guest was: next frame it appears beside the host again. */
  reset(): void {
    this.at = this.spot = this.anchor = this.last = null;
    this.motion.set(0, 0, 0);
    this.sideSign = 0;
    this.walking = false;
  }
}

/**
 * A spot FOLLOW.side beside the host where it can stand: of the bearings round it, the good one nearest where the
 * guest stands now (so it doesn't cross over), else its left; a little nearer or farther when none a step away is
 * (a host on a narrow pier). With nowhere to stand, its left. For a host standing still.
 */
export function spotBeside(host: HostAt, radius: number, dry: (dir: Vector3) => boolean, near: Vector3 | null): Vector3 {
  const forward = tangentToward(host.dir, host.heading.clone().add(host.dir), new Vector3());
  const leftOf = new Vector3().crossVectors(host.dir, forward).normalize();
  const t = new Vector3();
  for (const ring of RINGS) {
    let best: Vector3 | null = null;
    let bestD = Infinity;
    for (const bearing of AROUND) {
      t.copy(leftOf).applyAxisAngle(host.dir, bearing);
      const spot = travel(host.dir, t, (ring * FOLLOW.side) / radius, new Vector3());
      if (!dry(spot)) continue;
      if (!near) return spot;
      const d = angleBetween(spot, near);
      if (d < bestD) {
        bestD = d;
        best = spot;
      }
    }
    if (best) return best;
  }
  return travel(host.dir, leftOf, FOLLOW.side / radius, new Vector3());
}

/**
 * The side a guest walks on as its host sets off the way `going`: the side it stands on, well to one side of the
 * host's way; standing in its way (ahead of it or behind), the side with room to stand on, its left before its right,
 * so it steps aside rather than be walked into.
 */
export function setOffSide(host: Vector3, going: Vector3, at: Vector3, radius: number, dry: (dir: Vector3) => boolean): 1 | -1 {
  const left = new Vector3().crossVectors(host, going).normalize();
  const lateral = at.clone().sub(host).dot(left) * radius;
  if (Math.abs(lateral) > IN_THE_WAY) return lateral < 0 ? -1 : 1;
  const room = (sign: number) => ABREAST.some((ring) => dry(travel(host, left.clone().multiplyScalar(sign), (ring * FOLLOW.side) / radius, new Vector3())));
  if (room(1)) return 1;
  if (room(-1)) return -1;
  return lateral < 0 ? -1 : 1;
}

/**
 * A spot abreast of a host that walks the way `going` (a unit tangent at `host`): FOLLOW.side to the given side of
 * `at` (a place a touch ahead of it), or a little nearer or farther; with no room beside it, behind it on the way it
 * came (ground it has just walked), in single file. Never on its other side: crossing over, the guest would walk
 * through it.
 */
export function spotAbreast(host: Vector3, going: Vector3, at: Vector3, sign: 1 | -1, radius: number, dry: (dir: Vector3) => boolean): Vector3 {
  const side = new Vector3().crossVectors(host, going).normalize().multiplyScalar(sign);
  for (const ring of ABREAST) {
    const spot = travel(at, side, (ring * FOLLOW.side) / radius, new Vector3());
    if (dry(spot)) return spot;
  }
  const behind = going.clone().negate();
  for (const ring of BEHIND) {
    const spot = travel(host, behind, (ring * FOLLOW.side) / radius, new Vector3());
    if (dry(spot)) return spot;
  }
  return travel(host, behind, FOLLOW.side / radius, new Vector3());
}
